import {useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {useQuery, type UseQueryResult} from '@tanstack/react-query'

import Toast from 'components/Basic/Toast'
import {
  AIUsageAppsPerUserAPIResponseData,
  AIUsageAppsPerUserAPIResponse,
} from 'interfaces'
import {AIUsageService} from 'services/api'

export const AI_USAGE_APPS_PER_USER_QUERY_KEY = 'getAIUsageAppsPerUser'

export type UseAIUsageAppsPerUserQueryProps = {
  email: string
  enabled?: boolean
  /**
   * When true (default), shows the generic fetch error toast on failure.
   * Set false to handle errors in the caller (e.g. custom UI or messaging).
   */
  showErrorToast?: boolean
}

type UseAIUsageAppsPerUserQueryResult = Pick<
  UseQueryResult<AIUsageAppsPerUserAPIResponseData, Error>,
  'isLoading' | 'isSuccess' | 'isError' | 'error' | 'refetch'
> & {
  data?: AIUsageAppsPerUserAPIResponse
}

export const useAIUsageAppsPerUserQuery = ({
  email,
  enabled = true,
  showErrorToast = true,
}: UseAIUsageAppsPerUserQueryProps): UseAIUsageAppsPerUserQueryResult => {
  const {t} = useTranslation(['aiUsage'])
  const {data, isLoading, isSuccess, isError, error, refetch} = useQuery<
    AIUsageAppsPerUserAPIResponseData,
    Error
  >({
    queryKey: [AI_USAGE_APPS_PER_USER_QUERY_KEY, email],
    queryFn: () => {
      if (!email) {
        return Promise.resolve({data: {apps: []}})
      }
      return AIUsageService.getAIUsageAppsPerUser(email)
    },
    enabled: Boolean(enabled && email),
  })

  useEffect(() => {
    if (isError && showErrorToast) {
      Toast({
        message: t('aiUsage:AI_USAGE_TOAST_APPS_PER_USER_FETCH_ERROR'),
        type: 'error',
        customId: 'ai-usage-apps-per-user-fetch-error',
      })
    }
  }, [isError, showErrorToast, t])

  return {
    data: data?.data,
    isLoading,
    isSuccess,
    isError,
    error,
    refetch,
  }
}
